import React, { useEffect } from "react";
import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import { parse, stringify } from "qs";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import getProductsV3 from "api/requests/getProductsV3";
import { getUserLocationData } from "api/requests/getUserLocation";
import { Query } from "custom-types/Query";
import useSyncUrlWithLocation from "hooks/useSyncUrlWithLocation";
import { useDispatch } from "redux/hooks";
import {
  COMPLIANCE_RULESET_LOAD,
  ComplianceRuleset,
} from "redux/reducers/complianceRules";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import logError from "utils/logError";
import {
  LEGACY_FILTER_MAP,
  normalizeShopFilterParam,
} from "utils/normalizeShopFilterParam";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";
import syncUrlWithUserLocation from "utils/syncUrlWithUserLocation";

import { ShopItem } from "components/ShopItemCard/types";
import { AvailableFilter } from "components/UnifiedShop/DynamicFilters/types";
import UnifiedShop, {
  UNIFIED_SHOP_PAGE_SIZE,
} from "components/UnifiedShop/UnifiedShop";

export type ShopProps = {
  shopItems: ShopItem[];
  availableFilters: AvailableFilter[];
  totalCount: number;
  shopUrlLocationParamEnabled: boolean;
  complianceRuleset: ComplianceRuleset | null;
};

const ShopPage: React.FC<ShopProps> = ({
  complianceRuleset,
  shopUrlLocationParamEnabled,
  ...newProps
}) => {
  const dispatch = useDispatch();

  // this hook syncs any changes to the location with the url param
  useSyncUrlWithLocation(shopUrlLocationParamEnabled);

  useEffect(() => {
    if (complianceRuleset) {
      dispatch({
        complianceRuleset,
        type: COMPLIANCE_RULESET_LOAD,
      });
    }
  }, [dispatch]);

  return <UnifiedShop {...newProps} />;
};

export const getServerSideProps = createGetServerSideProps<ShopProps>(
  async (context) => {
    const [url, _hash] = context.resolvedUrl.split("#");
    const [path, query] = url.split("?");
    const {
      filter,
      fulfillment,
      q,
      sort,
      // * Ignore fulfillmentPreference as its no longer in use.
      fulfillmentPreference,
      ...others
    } = parse(query);

    const shouldUpdateFilters =
      filter &&
      Object.keys(filter).some((appliedFilter) =>
        Object.keys(LEGACY_FILTER_MAP).includes(appliedFilter),
      );
    if (shouldUpdateFilters || fulfillmentPreference) {
      const normalizedFilterParam = shouldUpdateFilters
        ? normalizeShopFilterParam(filter as Query)
        : undefined;

      const newQueryString = stringify(
        { filter: normalizedFilterParam, q, sort, ...others },
        { arrayFormat: "brackets", indices: false },
      );
      return {
        redirect: {
          destination: `${path}${newQueryString ? `?${newQueryString}` : ""}`,
          statusCode: 302,
        },
      };
    }

    const { page } = context.query;
    const currentPage = (parseInt(page as string) || 1) - 1;
    const countryCode = getCountryCodeHeader(context.req.headers);
    const { coordinates, state_code } = await getUserLocationData(
      countryCode,
      context,
    );

    // TODO Clean up this experiment when complete (https://leafly.atlassian.net/browse/SHH-941)
    const {
      "SHH-941_shopUrlLocation_flag": { treatment: shopUrlLocationFlag },
    } = await getServerSideSplitTreatments(
      "SHH-941_shopUrlLocation_flag",
      getSplitKey(context),
    );
    const shopUrlLocationParamEnabled = shopUrlLocationFlag === "on";

    if (shopUrlLocationParamEnabled) {
      const { redirect: locationRedirect } =
        await syncUrlWithUserLocation(context);

      if (locationRedirect) {
        return {
          redirect: locationRedirect,
        };
      }
    }

    const apiParams = {
      filter: filter as Query,
      fulfillment: fulfillment as string[],
      lat: coordinates?.latitude || coordinates?.lat,
      lon: coordinates?.longitude || coordinates?.lon,
      q: q as string,
      skip: currentPage * UNIFIED_SHOP_PAGE_SIZE,
      sort: sort as Query,
      take: UNIFIED_SHOP_PAGE_SIZE,
    };

    try {
      const [data, complianceRuleset] = await Promise.all([
        getProductsV3(apiParams),
        getRegionComplianceRules(state_code, countryCode),
      ]);

      return {
        props: {
          availableFilters: data?.metadata.availableFilters || [],
          complianceRuleset: complianceRuleset,
          disableFixedHeader: true,
          shopItems: data?.data || [],
          shopUrlLocationParamEnabled,
          totalCount: data?.metadata.totalCount || 0,
        },
      };
    } catch (error) {
      logError(error, {
        functionName: "/shop getServerSideProps",
      });
      return {
        props: {
          availableFilters: [],
          complianceRuleset: null,
          disableFixedHeader: true,
          shopItems: [],
          shopUrlLocationParamEnabled,
          totalCount: 0,
        },
      };
    }
  },
);

export default ShopPage;
